/**
 * Catalogul de blocuri de pagina al unei piete — sursa UNICA pentru trei capete.
 *
 * De ce exista fisierul asta, si de ce aici:
 *  - `web` randeaza blocurile, deci are nevoie de tipuri si de numele
 *    proprietatilor;
 *  - `core` valideaza configul publicat, deci are nevoie de aceleasi nume ca sa
 *    poata refuza un bloc inventat;
 *  - `Hub` deseneaza editorul, deci are nevoie de eticheta, de felul fiecarui
 *    camp si de valorile implicite.
 *
 * Hub-ul e alt depozit si nu poate importa pachetul asta. NU tine insa o a doua
 * copie a catalogului: il cere core-ului prin tool-ul MCP
 * `deliveryapp_page_block_catalog`, care serveste exact structura de mai jos.
 * Un catalog scris de doua ori ar diverge la primul bloc adaugat, iar divergenta
 * s-ar vedea abia in vitrina: editorul ar oferi un camp pe care randarea nu il
 * citeste, sau ar ascunde unul pe care ea il asteapta.
 *
 * ADR-011 ramane in vigoare si aici. Blocurile sunt PREZENTARE: text, culori,
 * ordine, comutatoare. Ce e entitate in core — vitrine, vendori, zone, meniuri,
 * oferte — NU intra in config nici macar ca identificator. Blocurile care arata
 * date reale nu tin o lista de id-uri, ci un SELECTOR („primele N vitrine",
 * „zonele active", „ofertele din categoria «Pizza»"): garda din
 * `assertConfigScalarsOnly` refuza oricum orice sir in forma de UUID, dar
 * motivul nu e garda, ci faptul ca o lista de id-uri intr-un blob republicat
 * ramane in urma realitatii fara ca cineva sa afle.
 */
export const BLOCK_CATEGORY_LABELS = {
    deschidere: "Deschidere",
    vitrina: "Restaurante și meniu",
    continut: "Conținut",
    incredere: "Încredere",
    actiune: "Îndemn la acțiune",
    practic: "Practic",
};
// ---------------------------------------------------------------------------
// Tipurile de bloc
// ---------------------------------------------------------------------------
export const MARKET_BLOCK_TYPES = [
    "hero",
    "hero-search",
    "address-bar",
    "storefront-grid",
    "menu-highlights",
    "coverage-list",
    "steps",
    "features",
    "text",
    "image-text",
    "gallery",
    "stats",
    "testimonials",
    "faq",
    "hours",
    "contact",
    "reservations",
    "cta-banner",
    "promo-strip",
    "payment-note",
    "legal-note",
    "divider",
];
export function isMarketBlockType(value) {
    return typeof value === "string" && MARKET_BLOCK_TYPES.includes(value);
}
// Optiuni reutilizate, ca doua blocuri sa nu ajunga cu vocabular diferit pentru
// acelasi lucru („stanga/left", „mare/large").
const ALIGN_OPTIONS = [
    { value: "stanga", label: "La stânga" },
    { value: "centru", label: "Centrat" },
];
const TONE_OPTIONS = [
    { value: "brand", label: "Culoarea principală" },
    { value: "surface", label: "Card, pe fundalul paginii" },
    { value: "contur", label: "Doar contur" },
];
const HEIGHT_OPTIONS = [
    { value: "compact", label: "Compactă" },
    { value: "normala", label: "Normală" },
    { value: "inalta", label: "Înaltă" },
];
const IMAGE_POSITION_PROP = {
    key: "imagePosition", label: "Partea importantă a fotografiei", kind: "select", defaultValue: "center",
    hint: "Alege zona care rămâne în cadru când fotografia este decupată pe telefon.",
    options: [{ value: "center", label: "Centru" }, { value: "top", label: "Sus" }, { value: "bottom", label: "Jos" }, { value: "left", label: "Stânga" }, { value: "right", label: "Dreapta" }],
};
const COLUMNS_OPTIONS = [
    { value: "2", label: "2 pe rând" },
    { value: "3", label: "3 pe rând" },
    { value: "4", label: "4 pe rând" },
];
/** Titlul si subtitlul apar pe aproape fiecare bloc; scrise o data. */
function headingProps(titleDefault, subtitleHint) {
    return [
        {
            key: "title",
            label: "Titlu",
            kind: "text",
            ...(titleDefault === undefined ? {} : { defaultValue: titleDefault }),
        },
        {
            key: "subtitle",
            label: "Subtitlu",
            kind: "text",
            ...(subtitleHint === undefined ? {} : { hint: subtitleHint }),
        },
    ];
}
export const MARKET_BLOCK_CATALOG = [
    // -------------------------------------------------------------- deschidere
    {
        type: "hero",
        label: "Copertă",
        category: "deschidere",
        description: "Imaginea mare de deschidere, cu titlu, text și un buton.",
        props: [
            ...headingProps(),
            { key: "eyebrow", label: "Text scurt deasupra titlului", kind: "text", hint: "De exemplu: Bucătărie de cartier · Comandă direct" },
            { key: "composition", label: "Stil copertă", kind: "select", options: [{ value: "classic", label: "Fotografie pe tot fundalul" }, { value: "split", label: "Editorial — text și fotografie alăturate" }], defaultValue: "classic" },
            { key: "imageUrl", label: "Imagine de fundal", kind: "image", hint: "Lăsată goală, coperta rămâne pe culoarea temei." },
            IMAGE_POSITION_PROP,
            { key: "ctaLabel", label: "Text buton", kind: "text" },
            { key: "ctaHref", label: "Unde duce butonul", kind: "url", hint: "O adresă completă sau o cale internă, ex. /p/despre." },
            { key: "secondaryLabel", label: "Al doilea buton (opțional)", kind: "text" },
            { key: "secondaryHref", label: "Destinația celui de-al doilea buton", kind: "url" },
            { key: "height", label: "Înălțime", kind: "select", options: HEIGHT_OPTIONS, defaultValue: "normala" },
            { key: "align", label: "Aliniere", kind: "select", options: ALIGN_OPTIONS, defaultValue: "stanga" },
            {
                key: "overlay",
                label: "Întunecare imagine (%)",
                kind: "number",
                min: 0,
                max: 85,
                defaultValue: 55,
                hint: "Fără ea, un titlu alb peste o poză deschisă devine ilizibil.",
            },
        ],
    },
    {
        type: "hero-search",
        label: "Copertă cu adresă",
        category: "deschidere",
        description: "Copertă cu bara de adresă în interior — deschiderea obișnuită a unei platforme de livrare.",
        dataNote: "Adresa introdusă aici e aceeași cu cea folosită la finalizarea comenzii.",
        props: [
            ...headingProps("Ce mănânci azi?"),
            { key: "eyebrow", label: "Text scurt deasupra titlului", kind: "text" },
            { key: "composition", label: "Stil copertă", kind: "select", options: [{ value: "classic", label: "Fotografie pe tot fundalul" }, { value: "split", label: "Editorial — text și fotografie alăturate" }], defaultValue: "classic" },
            { key: "ctaLabel", label: "Buton spre meniu (opțional)", kind: "text" },
            { key: "ctaHref", label: "Destinația butonului", kind: "url", hint: "Ex.: /p/meniu. Adresa rămâne necesară pentru verificarea livrării." },
            { key: "imageUrl", label: "Imagine de fundal", kind: "image" },
            IMAGE_POSITION_PROP,
            { key: "addressLabel", label: "Text lângă câmpul de adresă", kind: "text", defaultValue: "Unde livrăm?" },
            { key: "addressCta", label: "Text buton adresă", kind: "text", defaultValue: "Vezi restaurantele" },
            { key: "height", label: "Înălțime", kind: "select", options: HEIGHT_OPTIONS, defaultValue: "inalta" },
            { key: "overlay", label: "Întunecare imagine (%)", kind: "number", min: 0, max: 85, defaultValue: 55 },
        ],
    },
    {
        type: "address-bar",
        label: "Bară de adresă",
        category: "deschidere",
        description: "Un singur rând cu adresa clientului și butonul de schimbare.",
        dataNote: "Arată adresa salvată în sesiune; nu confirmă acoperirea, aceea se verifică la comandă.",
        props: [
            { key: "title", label: "Titlu când nu există adresă", kind: "text", defaultValue: "Unde livrăm?" },
            { key: "hint", label: "Text ajutător", kind: "text", defaultValue: "Adaugă adresa ca să vezi dacă ajungem la tine" },
        ],
    },
    // ----------------------------------------------------------------- vitrina
    {
        type: "storefront-grid",
        label: "Restaurante",
        category: "vitrina",
        description: "Cardurile restaurantelor de pe piață, cu poză și legătură către meniu.",
        dataNote: "Vitrinele active ale pieței. Cu un singur restaurant, titlul secțiunii nu se mai afișează — "
            + "nu e nimic de ales. Fără nicio vitrină publicată, blocul spune asta în loc să dispară.",
        props: [
            ...headingProps("De unde comanzi"),
            {
                key: "layout",
                label: "Aranjare",
                kind: "select",
                options: [
                    { value: "grila", label: "Grilă" },
                    { value: "carusel", label: "Carusel pe orizontală" },
                    { value: "lista", label: "Listă pe un rând" },
                ],
                defaultValue: "grila",
            },
            { key: "columns", label: "Câte pe rând", kind: "select", options: COLUMNS_OPTIONS, defaultValue: "2" },
            { key: "limit", label: "Câte se arată", kind: "number", min: 0, max: 60, hint: "0 sau gol = toate." },
            {
                key: "showKind",
                label: "Arată detaliile de livrare pe card",
                kind: "boolean",
                defaultValue: true,
                // Cheia s-a numit așa cât timp controla eticheta de tip a vitrinei
                // („Mai multe branduri", „Marketplace"). Etichetele acelea descriau
                // organizarea internă a platformei, nu ceva ce alege clientul, și au
                // fost scoase. Numele cheii rămâne ca să nu invalideze configurările
                // deja publicate care o poartă; azi comută rândul cu timpul estimat,
                // taxa și minimul de pe card.
                hint: "Timpul estimat, taxa și comanda minimă, sub numele restaurantului.",
            },
            {
                key: "requireAddress",
                label: "Arată restaurantele doar după ce clientul și-a pus adresa",
                kind: "boolean",
                defaultValue: true,
                // Implicit PORNIT: fără adresă cardurile arată valori „de la" și nu pot
                // spune cine ajunge la client; lista întreagă înainte de adresă trimitea
                // omul într-un meniu din care afla abia la coș că nu se livrează la el.
                hint: "Până la adresă, blocul arată un singur card: «Unde livrăm?». După adresă, restaurantele care ajung la ea.",
            },
            {
                key: "hideUndeliverable",
                label: "Ascunde restaurantele care nu livrează la adresa clientului",
                kind: "boolean",
                defaultValue: true,
                hint: "Oprit, cele care nu ajung rămân în listă, la coadă și marcate. Pornit, se pot vedea la cerere, dintr-un buton.",
            },
            {
                key: "showSearch",
                label: "Câmp de căutare după nume și tip de bucătărie",
                kind: "boolean",
                defaultValue: false,
                hint: "Util de la cinci-șase restaurante în sus. Cu mai puține, lista se vede oricum întreagă.",
            },
            {
                key: "showFilters",
                label: "Filtre pe tip de bucătărie",
                kind: "boolean",
                defaultValue: false,
                hint: "Chipuri («Pizza», «Burgeri») deduse din meniurile publicate. Fără etichete deduse, rândul nu apare.",
            },
            {
                key: "sortBy",
                label: "Ordinea cardurilor",
                kind: "select",
                options: [
                    { value: "recommended", label: "Recomandată (cu adresa: cele mai rapide primele)" },
                    { value: "eta", label: "Cele mai rapide" },
                    { value: "fee", label: "Livrare ieftină" },
                    { value: "name", label: "Alfabetic" },
                ],
                defaultValue: "recommended",
                hint: "Restaurantele care nu livrează la adresa clientului coboară mereu la coadă, indiferent de ordine.",
            },
        ],
    },
    {
        type: "menu-highlights",
        label: "Din meniu",
        category: "vitrina",
        description: "Câteva produse reale, luate din meniul publicat, cu poză și preț.",
        dataNote: "Ofertele primei vitrine (sau ale celei numite mai jos). Produsele indisponibile nu apar. "
            + "Fără meniu publicat, blocul nu se afișează deloc.",
        props: [
            ...headingProps("Recomandările noastre"),
            {
                key: "requireAddress",
                label: "Arată produsele doar după ce clientul și-a pus adresa",
                kind: "boolean",
                defaultValue: true,
                hint: "Aceeași regulă ca la lista de restaurante: fără adresă, blocul nu se afișează.",
            },
            {
                key: "storefrontSlug",
                label: "Din ce vitrină",
                kind: "text",
                hint: "Numele tehnic al vitrinei. Lăsat gol, se ia prima vitrină a pieței.",
            },
            {
                key: "category",
                label: "Doar din categoria",
                kind: "text",
                hint: "Numele categoriei, așa cum apare în meniu. Gol = din toate.",
            },
            { key: "limit", label: "Câte produse", kind: "number", min: 1, max: 24, defaultValue: 6 },
            { key: "columns", label: "Câte pe rând", kind: "select", options: COLUMNS_OPTIONS, defaultValue: "3" },
            { key: "ctaLabel", label: "Text buton spre meniu", kind: "text", defaultValue: "Vezi tot meniul" },
        ],
    },
    {
        type: "coverage-list",
        label: "Unde livrăm",
        category: "vitrina",
        description: "Localitățile și zonele în care piața livrează, cu taxa fiecăreia.",
        dataNote: "Zonele active din core. Fără nicio zonă activă, blocul nu se afișează.",
        props: [
            ...headingProps("Unde livrăm"),
            { key: "showFees", label: "Arată taxa de livrare", kind: "boolean", defaultValue: true },
            { key: "showMinOrder", label: "Arată comanda minimă", kind: "boolean", defaultValue: false },
            {
                key: "groupByCity",
                label: "Grupează pe localități",
                kind: "boolean",
                defaultValue: true,
                hint: "Util când piața acoperă mai multe orașe.",
            },
        ],
    },
    // ---------------------------------------------------------------- continut
    {
        type: "steps",
        label: "Cum funcționează",
        category: "continut",
        description: "Pași numerotați. Explică fluxul comenzii înainte ca cineva să întrebe.",
        props: [
            ...headingProps("Cum funcționează"),
            {
                key: "items",
                label: "Pași",
                kind: "items",
                maxItems: 6,
                itemFields: [
                    { key: "title", label: "Titlu pas", kind: "text" },
                    { key: "body", label: "Explicație", kind: "longtext" },
                ],
                defaultValue: [
                    { title: "Adresa", body: "În funcție de ea verificăm dacă livrăm acolo și ce restaurante te pot deservi." },
                    {
                        title: "Comanda",
                        // „inainte sa confirmi" nu e umplutura: ADR-003 cere ca vanzatorul sa
                        // fie cunoscut INAINTE de a contracta, iar propozitia asta e locul in
                        // care pagina o spune clientului.
                        body: "La finalizare vezi denumirea completă, CUI-ul și sediul restaurantului care îți vinde, înainte să confirmi.",
                    },
                    { title: "Livrarea", body: "Curierul aduce comanda împreună cu bonul fiscal emis de restaurant." },
                ],
            },
        ],
    },
    {
        type: "features",
        label: "Avantaje",
        category: "continut",
        description: "O grilă de motive pentru care merită comandat de aici.",
        props: [
            ...headingProps(),
            { key: "columns", label: "Câte pe rând", kind: "select", options: COLUMNS_OPTIONS, defaultValue: "3" },
            {
                key: "items",
                label: "Avantaje",
                kind: "items",
                maxItems: 9,
                itemFields: [
                    { key: "icon", label: "Pictogramă (emoji)", kind: "text", placeholder: "🍕" },
                    { key: "title", label: "Titlu", kind: "text" },
                    { key: "body", label: "Text", kind: "longtext" },
                ],
            },
        ],
    },
    {
        type: "text",
        label: "Text",
        category: "continut",
        description: "Un titlu și câteva paragrafe. Rândurile goale despart paragrafele.",
        props: [
            ...headingProps(),
            { key: "body", label: "Text", kind: "longtext" },
            { key: "align", label: "Aliniere", kind: "select", options: ALIGN_OPTIONS, defaultValue: "stanga" },
            { key: "narrow", label: "Coloană îngustă", kind: "boolean", defaultValue: true, hint: "Rânduri mai scurte se citesc mai ușor." },
        ],
    },
    {
        type: "image-text",
        label: "Imagine și text",
        category: "continut",
        description: "O imagine lângă un text, cu buton opțional.",
        props: [
            ...headingProps(),
            { key: "body", label: "Text", kind: "longtext" },
            { key: "imageUrl", label: "Imagine", kind: "image" },
            IMAGE_POSITION_PROP,
            {
                key: "imageSide",
                label: "Imaginea stă",
                kind: "select",
                options: [
                    { value: "stanga", label: "La stânga" },
                    { value: "dreapta", label: "La dreapta" },
                ],
                defaultValue: "stanga",
            },
            { key: "ctaLabel", label: "Text buton", kind: "text" },
            { key: "ctaHref", label: "Unde duce butonul", kind: "url" },
        ],
    },
    {
        type: "gallery",
        label: "Galerie",
        category: "continut",
        description: "Un șir de imagini, cu descriere opțională.",
        props: [
            { key: "layout", label: "Aranjare fotografii", kind: "select", options: [{ value: "grid", label: "Grilă uniformă" }, { value: "editorial", label: "Editorial — prima fotografie mare" }], defaultValue: "grid" },
            ...headingProps(),
            {
                key: "items",
                label: "Imagini",
                kind: "items",
                maxItems: 12,
                itemFields: [
                    { key: "imageUrl", label: "Imagine", kind: "image" },
                    { key: "caption", label: "Descriere", kind: "text" },
                ],
            },
        ],
    },
    // --------------------------------------------------------------- incredere
    {
        type: "stats",
        label: "Cifre",
        category: "incredere",
        description: "Câteva cifre mari. Scrise de mână — nu se calculează din comenzi.",
        props: [
            ...headingProps(),
            {
                key: "items",
                label: "Cifre",
                kind: "items",
                maxItems: 6,
                itemFields: [
                    { key: "value", label: "Cifra", kind: "text", placeholder: "1200+" },
                    { key: "label", label: "Ce înseamnă", kind: "text", placeholder: "comenzi livrate" },
                ],
            },
        ],
    },
    {
        type: "testimonials",
        label: "Ce spun clienții",
        category: "incredere",
        description: "Citate de la clienți. Se scriu aici; nu vin din recenzii.",
        props: [
            ...headingProps("Ce spun clienții"),
            {
                key: "items",
                label: "Citate",
                kind: "items",
                maxItems: 9,
                itemFields: [
                    { key: "quote", label: "Citat", kind: "longtext" },
                    { key: "author", label: "Cine", kind: "text" },
                    { key: "role", label: "Detaliu", kind: "text", placeholder: "Timișoara" },
                ],
            },
        ],
    },
    {
        type: "faq",
        label: "Întrebări frecvente",
        category: "incredere",
        description: "Întrebări și răspunsuri, pliabile.",
        props: [
            ...headingProps("Întrebări frecvente"),
            {
                key: "items",
                label: "Întrebări",
                kind: "items",
                maxItems: 12,
                itemFields: [
                    { key: "question", label: "Întrebare", kind: "text" },
                    { key: "answer", label: "Răspuns", kind: "longtext" },
                ],
            },
        ],
    },
    // ----------------------------------------------------------------- practic
    {
        type: "hours",
        label: "Program",
        category: "practic",
        description: "Programul pieței, pe zile.",
        dataNote: "Programul săptămânal și zilele speciale ale aplicației. Fără program configurat, blocul nu se afișează.",
        props: [...headingProps("Program"), { key: "note", label: "Mențiune sub program", kind: "text" }],
    },
    {
        type: "contact",
        label: "Contact",
        category: "practic",
        description: "Telefon, email și adresă, cu legături care chiar sună și deschid harta.",
        dataNote: "Telefonul și emailul pentru clienți din setările aplicației. Adresa se scrie aici.",
        props: [
            ...headingProps("Contact"),
            { key: "showPhone", label: "Arată telefonul de suport", kind: "boolean", defaultValue: true },
            { key: "showEmail", label: "Arată emailul de suport", kind: "boolean", defaultValue: true },
            { key: "address", label: "Adresă", kind: "text" },
            { key: "mapUrl", label: "Legătură hartă", kind: "url" },
        ],
    },
    {
        type: "reservations",
        label: "Rezervare masă",
        category: "practic",
        description: "Formular de rezervare la restaurantele care primesc rezervări.",
        dataNote: "Restaurantele cu rezervări active, sloturile lor reale și limitele lor de persoane. "
            + "Niciun restaurant cu rezervări → blocul nu se afișează.",
        props: [
            ...headingProps("Rezervă o masă"),
            { key: "note", label: "Mențiune sub formular", kind: "longtext" },
        ],
    },
    {
        type: "payment-note",
        label: "Cum se plătește",
        category: "practic",
        description: "Metodele de plată acceptate, într-un rând.",
        dataNote: "Metodele configurate pe piață. O metodă pe care aplicația nu o poate numi nu se afișează.",
        props: [{ key: "title", label: "Titlu", kind: "text" }],
    },
    {
        type: "legal-note",
        label: "Mențiune legală",
        category: "practic",
        description: "Text mărunt, pentru mențiuni obligatorii.",
        props: [{ key: "body", label: "Text", kind: "longtext" }],
    },
    {
        type: "divider",
        label: "Spațiu",
        category: "practic",
        description: "Un spațiu, cu sau fără linie.",
        props: [
            {
                key: "size",
                label: "Mărime",
                kind: "select",
                options: [
                    { value: "mic", label: "Mic" },
                    { value: "normal", label: "Normal" },
                    { value: "mare", label: "Mare" },
                ],
                defaultValue: "normal",
            },
            { key: "line", label: "Cu linie", kind: "boolean", defaultValue: false },
        ],
    },
    // ----------------------------------------------------------------- actiune
    {
        type: "cta-banner",
        label: "Bandă de îndemn",
        category: "actiune",
        description: "O bandă lată cu un mesaj și un buton.",
        props: [
            ...headingProps(),
            { key: "body", label: "Text", kind: "longtext" },
            { key: "ctaLabel", label: "Text buton", kind: "text" },
            { key: "ctaHref", label: "Unde duce butonul", kind: "url" },
            { key: "tone", label: "Fundal", kind: "select", options: TONE_OPTIONS, defaultValue: "brand" },
        ],
    },
    {
        type: "promo-strip",
        label: "Bandă de anunț",
        category: "actiune",
        description: "Un rând subțire cu un anunț scurt. Bun pentru o promoție sau un program special.",
        props: [
            { key: "message", label: "Mesaj", kind: "text" },
            { key: "ctaLabel", label: "Text legătură", kind: "text" },
            { key: "ctaHref", label: "Unde duce", kind: "url" },
            { key: "tone", label: "Fundal", kind: "select", options: TONE_OPTIONS, defaultValue: "brand" },
        ],
    },
];
const SPEC_BY_TYPE = new Map(MARKET_BLOCK_CATALOG.map((spec) => [spec.type, spec]));
export function findBlockSpec(type) {
    return SPEC_BY_TYPE.get(type) ?? null;
}
/**
 * Configul cu care se naste un bloc adaugat din editor. Se construieste din
 * `defaultValue`-urile catalogului, nu dintr-o a doua lista de valori: doua
 * liste ar fi divergat, iar blocul nou ar fi aparut cu alte valori decat cele
 * pe care le anunta editorul.
 */
export function defaultBlockConfig(type) {
    const spec = findBlockSpec(type);
    if (spec === null)
        return {};
    const out = {};
    for (const prop of spec.props) {
        if (prop.defaultValue === undefined)
            continue;
        out[prop.key] = Array.isArray(prop.defaultValue)
            ? prop.defaultValue.map((item) => ({ ...item }))
            : prop.defaultValue;
    }
    return out;
}
// ---------------------------------------------------------------------------
// Citirea valorilor (folosita de web la randare)
// ---------------------------------------------------------------------------
/**
 * Configul vine dintr-un JSON publicat: la rulare poate contine orice, indiferent
 * ce spune tipul. Citirile de mai jos nu presupun nimic si nu „curata" valori —
 * ce nu e de felul cerut se ignora si se cade pe valoarea de rezerva a randarii.
 */
export function blockText(block, key, fallback = "") {
    const value = block.config[key];
    return typeof value === "string" && value.trim().length > 0 ? value : fallback;
}
export function blockNumber(block, key, fallback) {
    const value = block.config[key];
    if (typeof value === "number" && Number.isFinite(value))
        return value;
    // Editorul poate trimite numarul ca text (un `<input>` produce siruri).
    if (typeof value === "string" && value.trim().length > 0) {
        const parsed = Number(value);
        if (Number.isFinite(parsed))
            return parsed;
    }
    return fallback;
}
export function blockFlag(block, key, fallback) {
    const value = block.config[key];
    if (typeof value === "boolean")
        return value;
    if (value === "true")
        return true;
    if (value === "false")
        return false;
    return fallback;
}
export function blockItems(block, key = "items") {
    // Tipul spune `Array<Record<string,string>>`, dar valoarea vine dintr-un JSON
    // publicat: la rulare poate contine orice. Se citeste ca `unknown` tocmai ca
    // verificarile de mai jos sa fie reale, nu formale.
    const value = block.config[key];
    if (!Array.isArray(value))
        return [];
    const out = [];
    for (const raw of value) {
        if (raw === null || typeof raw !== "object" || Array.isArray(raw))
            continue;
        const item = {};
        for (const [field, fieldValue] of Object.entries(raw)) {
            if (typeof fieldValue === "string")
                item[field] = fieldValue;
        }
        out.push(item);
    }
    return out;
}
/** Slug-ul paginii de start, in forma canonica. */
export const HOME_PAGE_SLUG = "";
export function blockPageSlug(block) {
    const raw = block.page;
    return typeof raw === "string" ? raw.trim().toLowerCase() : HOME_PAGE_SLUG;
}
/** Blocurile vizibile ale unei pagini, in ordinea din config. */
export function blocksForPage(blocks, pageSlug) {
    if (!Array.isArray(blocks))
        return [];
    const wanted = pageSlug.trim().toLowerCase();
    return blocks.filter((block) => block.visible !== false && blockPageSlug(block) === wanted);
}
/**
 * Ajutor de scriere: blocurile template-urilor au id-uri scurte si stabile.
 *
 * Configul PORNESTE de la valorile implicite ale catalogului, peste care se scrie
 * ce declara template-ul. Fara asta, un bloc pentru care template-ul dadea doar
 * titlul (`steps`, `faq`, `gallery`) ajungea fara elemente, iar randarea lui
 * intoarce `null` la lista goala: operatorul aplica un template descris ca „asa
 * arata un agregator" si primea o pagina din care lipseau sectiuni promise, fara
 * niciun semnal.
 */
function block(id, type, config, page) {
    const merged = { ...defaultBlockConfig(type), ...config };
    return page === undefined ? { id, type, config: merged } : { id, type, page, config: merged };
}
export const MARKET_TEMPLATES = [
    {
        id: "bistro-local",
        name: "Bistro de cartier",
        audience: "Un restaurant, o bucătărie cu personalitate, comenzi directe.",
        description: "Copertă editorială, meniu la un pas și trei pagini pentru poveste, livrare și contact. Adaugă fotografiile tale și personalizează textele înainte de publicare.",
        theme: { primaryColor: "#285348", secondaryColor: "#183b32", accentColor: "#c7774d", backgroundColor: "#fcf8f1", textColor: "#262b25", borderRadius: "rounded" },
        pages: [
            { slug: "", title: "Acasă", description: "Descoperă meniul și comandă direct de la restaurantul tău local." },
            { slug: "meniu", title: "Meniu & comenzi", navLabel: "Meniu", showInNav: true },
            { slug: "poveste", title: "Povestea noastră", navLabel: "Povestea", showInNav: true },
            { slug: "livrare", title: "Livrare & întrebări", navLabel: "Livrare", showInNav: true },
            { slug: "contact", title: "Hai la noi", navLabel: "Contact", showInNav: true },
        ],
        blocks: [
            block("bun-venit", "hero-search", { eyebrow: "Restaurantul tău de cartier", title: "O masă bună. Oriunde ești.", subtitle: "Alege ceva pe gustul tău. Noi ne ocupăm de restul.", composition: "split", height: "normala", addressCta: "Verifică livrarea", ctaLabel: "Explorează meniul", ctaHref: "/meniu" }),
            block("restaurante", "storefront-grid", { title: "Bucătăria noastră", layout: "lista", requireAddress: false }),
            block("recomandari", "menu-highlights", { title: "Cu ce te tentăm azi", subtitle: "Descoperă preparatele din meniul nostru.", requireAddress: false, limit: 4, columns: "4" }),
            block("poveste-scurta", "image-text", { title: "Mai aproape de bucătăria noastră", body: "O masă are și o poveste. Te invităm să o descoperi pe a noastră.", imageSide: "dreapta", ctaLabel: "Cunoaște-ne", ctaHref: "/p/poveste" }),
            block("pofta", "cta-banner", { title: "Ceva bun începe cu o alegere.", body: "Meniul e la un pas. Adaugă adresa pentru detaliile livrării.", ctaLabel: "Alege din meniu", ctaHref: "/meniu" }),
            block("meniu-adresa", "address-bar", {}, "meniu"),
            block("meniu-restaurante", "storefront-grid", { title: "Alege meniul", layout: "lista", requireAddress: false }, "meniu"),
            block("poveste-coperta", "hero", { eyebrow: "Din bucătăria noastră", title: "Mâncarea ne aduce împreună.", subtitle: "Un loc, oameni și gusturi pe care le descoperi cu fiecare vizită.", composition: "split", ctaLabel: "Vezi meniul", ctaHref: "/meniu" }, "poveste"),
            block("poveste-oameni", "image-text", { title: "Oamenii din spatele farfuriilor", body: "Scrie aici cine gătește, cum a început restaurantul și ce vă place să puneți pe masă.", imageSide: "stanga" }, "poveste"),
            block("poveste-galerie", "gallery", { title: "O privire în lumea noastră", layout: "editorial", items: [] }, "poveste"),
            block("livrare-coperta", "hero", { eyebrow: "De la noi la tine", title: "Tu alegi masa. Noi aducem gustul.", subtitle: "Verifică adresa, apoi alege din meniu.", ctaLabel: "Comandă online", ctaHref: "/meniu", height: "compact" }, "livrare"),
            block("livrare-adresa", "address-bar", {}, "livrare"),
            block("livrare-zone", "coverage-list", { title: "Ajungem în cartierul tău?", showFees: true, showMinOrder: true }, "livrare"),
            block("livrare-intrebari", "faq", { title: "Bine de știut", items: [
                    { question: "Cum aflu dacă livrați la mine?", answer: "Adaugă adresa de livrare. Disponibilitatea și costurile se verifică pentru adresa și comanda ta." },
                    { question: "Unde văd ingredientele și alergenii?", answer: "Deschide preparatul din meniu pentru detalii, înainte să îl adaugi în coș." },
                    { question: "Cum urmăresc comanda?", answer: "După confirmare, poți urmări starea comenzii în pagina ei." },
                ] }, "livrare"),
            block("contact-coperta", "hero", { title: "La o masă. Sau la un telefon distanță.", subtitle: "Ne bucurăm să te auzim.", height: "compact" }, "contact"),
            block("contact-detalii", "contact", { title: "Vorbește cu noi", showPhone: true, showEmail: true }, "contact"),
            block("contact-program", "hours", { title: "Când ne găsești" }, "contact"),
        ],
    },
    {
        id: "food-club-local",
        name: "Food club local",
        audience: "Câteva restaurante din aceeași zonă, fiecare cu propriul meniu.",
        description: "O piață locală cu personalitate: adresă, restaurante, o pagină despre comunitate și un ghid de livrare. Fără aglomerația unui agregator național.",
        theme: { primaryColor: "#a13f2c", secondaryColor: "#492a23", accentColor: "#edb552", backgroundColor: "#fffaf3", textColor: "#30251f", borderRadius: "pill" },
        pages: [
            { slug: "", title: "Acasă" },
            { slug: "meniu", title: "Restaurante & meniuri", navLabel: "Restaurante", showInNav: true },
            { slug: "comunitate", title: "Gusturi din apropiere", navLabel: "Despre noi", showInNav: true },
            { slug: "livrare", title: "Cum ajunge comanda la tine", navLabel: "Livrare", showInNav: true },
        ],
        blocks: [
            block("club-coperta", "hero-search", { eyebrow: "Gusturi locale. Un singur loc.", title: "Cartierul tău are gust.", subtitle: "Restaurante din apropiere, reunite pentru următoarea ta masă.", composition: "split", height: "normala", addressCta: "Vezi cine livrează", ctaLabel: "Descoperă restaurantele", ctaHref: "/meniu" }),
            block("club-restaurante", "storefront-grid", { title: "Azi, de unde comanzi?", subtitle: "Alege bucătăria care îți face poftă.", requireAddress: false, columns: "2" }),
            block("club-poveste", "image-text", { title: "Locuri pe care merită să le descoperi.", body: "O platformă pentru gusturile din apropierea ta. Fiecare restaurant își aduce propria poveste în meniu.", ctaLabel: "Descoperă comunitatea", ctaHref: "/p/comunitate", imageSide: "dreapta" }),
            block("club-adresa", "address-bar", {}, "meniu"),
            block("club-meniuri", "storefront-grid", { title: "Restaurantele din apropiere", requireAddress: false, columns: "2", showSearch: true, showFilters: true }, "meniu"),
            block("comunitate-coperta", "hero", { eyebrow: "La noi în cartier", title: "Mai multe bucătării. O comunitate.", composition: "split", ctaLabel: "Alege un restaurant", ctaHref: "/meniu" }, "comunitate"),
            block("comunitate-text", "text", { title: "Cunoaște locurile de la care comanzi", body: "Prezintă aici restaurantele, oamenii și zona pe care o deserviți. Adaugă fotografii reale și spune ce face fiecare bucătărie specială." }, "comunitate"),
            block("comunitate-galerie", "gallery", { title: "Oameni, locuri, farfurii", layout: "editorial", items: [] }, "comunitate"),
            block("comunitate-contact", "contact", { title: "Hai să vorbim", showPhone: true, showEmail: true }, "comunitate"),
            block("ghid-coperta", "hero", { title: "De la poftă la prima înghițitură.", ctaLabel: "Alege din meniu", ctaHref: "/meniu", height: "compact" }, "livrare"),
            block("ghid-adresa", "address-bar", {}, "livrare"),
            block("ghid-zone", "coverage-list", { title: "Unde livrăm", showFees: true, showMinOrder: true }, "livrare"),
            block("ghid-pasi", "steps", {}, "livrare"),
            block("ghid-contact", "contact", { title: "Ai nevoie de ajutor?", showPhone: true, showEmail: true }, "livrare"),
        ],
    },
    {
        id: "piata-oras",
        name: "Piață de oraș",
        audience: "Mai multe restaurante dintr-un oraș, sub un singur brand de platformă.",
        description: "Deschidere cu adresa în copertă, restaurantele imediat sub ea, apoi zonele acoperite și "
            + "explicația fluxului. Structura pe care o are un agregator de livrare.",
        theme: {
            primaryColor: "#0f7b6c",
            secondaryColor: "#124e4a",
            accentColor: "#f2a51a",
            borderRadius: "rounded",
        },
        pages: [{ slug: HOME_PAGE_SLUG, title: "Acasă" }],
        blocks: [
            block("hero", "hero-search", {
                title: "Ce mănânci azi?",
                subtitle: "Restaurantele orașului, într-un singur loc. Bonul fiscal îl emite restaurantul care gătește.",
                addressLabel: "Unde livrăm?",
                addressCta: "Vezi restaurantele",
                height: "inalta",
                overlay: 55,
            }),
            block("vitrine", "storefront-grid", {
                title: "Restaurante",
                subtitle: "Alege de unde comanzi.",
                layout: "grila",
                columns: "2",
                showKind: true,
            }),
            block("acoperire", "coverage-list", {
                title: "Unde livrăm",
                showFees: true,
                showMinOrder: true,
                groupByCity: true,
            }),
            block("pasi", "steps", {
                title: "Cum funcționează",
                items: [
                    { title: "Adresa", body: "În funcție de ea verificăm dacă livrăm acolo și ce restaurante te pot deservi." },
                    { title: "Comanda", body: "La finalizare vezi denumirea completă, CUI-ul și sediul restaurantului care îți vinde, înainte să confirmi." },
                    { title: "Livrarea", body: "Curierul aduce comanda împreună cu bonul fiscal emis de restaurant." },
                ],
            }),
            block("plata", "payment-note", { title: "Cum se plătește" }),
            block("intrebari", "faq", {
                title: "Întrebări frecvente",
                items: [
                    { question: "Cine emite bonul fiscal?", answer: "Restaurantul care gătește. El e vânzătorul, iar datele lui complete apar la finalizarea comenzii." },
                    { question: "Cât durează livrarea?", answer: "Intervalul estimat apare înainte de confirmare și ține cont de coada bucătăriei și de distanță." },
                    { question: "Pot plăti cu cardul?", answer: "Deocamdată se plătește numerar la livrare. Metodele acceptate sunt scrise pe pagina de finalizare." },
                ],
            }),
        ],
    },
    {
        id: "grup-restaurante",
        name: "Grup de restaurante",
        audience: "Mai multe firme sau localuri ale aceluiași proprietar, cu site comun și livrare.",
        description: "Un site de grup: copertă de brand, localurile ca vitrine, o pagină despre grup, rezervări "
            + "și contact. Livrarea e una dintre funcții, nu singura.",
        theme: {
            primaryColor: "#8c2f39",
            secondaryColor: "#3b1c22",
            accentColor: "#c9a227",
            backgroundColor: "#fbf8f4",
            borderRadius: "sharp",
        },
        pages: [
            { slug: HOME_PAGE_SLUG, title: "Acasă" },
            { slug: "despre", title: "Despre noi", navLabel: "Despre", showInNav: true },
            { slug: "rezervari", title: "Rezervări", showInNav: true },
            { slug: "contact", title: "Contact", showInNav: true },
        ],
        blocks: [
            block("hero", "hero", {
                title: "Localurile noastre",
                subtitle: "Aceeași bucătărie, în mai multe locuri. Comandă acasă sau rezervă o masă.",
                height: "inalta",
                align: "centru",
                overlay: 50,
                ctaLabel: "Comandă acum",
                ctaHref: "#vitrine",
            }),
            block("vitrine", "storefront-grid", {
                title: "Comandă de la",
                subtitle: "Fiecare local gătește și emite bonul propriu.",
                layout: "grila",
                columns: "2",
                showKind: true,
            }),
            block("meniu", "menu-highlights", {
                title: "Din meniu",
                limit: 6,
                columns: "3",
                ctaLabel: "Vezi tot meniul",
            }),
            block("despre-scurt", "image-text", {
                title: "Gătim de 12 ani în același loc",
                body: "Scrie aici povestea grupului: de unde a început, ce vă deosebește, cine gătește.\n\nRândurile goale despart paragrafele.",
                imageSide: "stanga",
                ctaLabel: "Despre noi",
                ctaHref: "/p/despre",
            }),
            block("acoperire", "coverage-list", { title: "Unde livrăm", showFees: true, groupByCity: true }),
            block("program", "hours", { title: "Program" }),
            block("despre-titlu", "text", {
                title: "Despre noi",
                body: "Povestea completă a grupului.\n\nÎnlocuiește textul acesta cu al vostru.",
                narrow: true,
            }, "despre"),
            // Fara galerie in template: un bloc de imagini pornit gol nu randeaza nimic,
            // iar adresele de poza nu se pot inventa. Operatorul o adauga din editor
            // cand are pozele — atunci alegerea e a lui, nu o promisiune neonorata.
            block("despre-text-2", "text", {
                title: "Cum lucrăm",
                body: "Scrie aici ce vă deosebește: de unde vin ingredientele, cine gătește, cum se pregătește comanda.",
            }, "despre"),
            block("despre-cifre", "stats", {
                items: [
                    { value: "12", label: "ani de bucătărie" },
                    { value: "3", label: "localuri" },
                    { value: "40k+", label: "clienți serviți" },
                ],
            }, "despre"),
            block("rezervari-form", "reservations", {
                title: "Rezervă o masă",
                subtitle: "Alege localul, ziua și ora. Confirmarea vine pe telefon.",
            }, "rezervari"),
            block("rezervari-program", "hours", { title: "Program" }, "rezervari"),
            block("contact-date", "contact", {
                title: "Contact",
                subtitle: "Pentru orice problemă cu o comandă, sună-ne.",
                showPhone: true,
                showEmail: true,
            }, "contact"),
        ],
    },
    {
        id: "restaurant-livrare",
        name: "Un restaurant, cu livrare",
        audience: "Un singur restaurant care vrea site propriu, comandă online și rezervări.",
        description: "Arată ca site-ul restaurantului, nu ca o platformă: copertă, meniul în pagină, "
            + "povestea, rezervări, program și contact.",
        theme: {
            primaryColor: "#1f6f4a",
            secondaryColor: "#123d2a",
            accentColor: "#e8b44a",
            backgroundColor: "#fdfaf6",
            borderRadius: "rounded",
        },
        pages: [
            { slug: HOME_PAGE_SLUG, title: "Acasă" },
            { slug: "meniu", title: "Meniu", showInNav: true },
            { slug: "rezervari", title: "Rezervări", showInNav: true },
            { slug: "contact", title: "Contact", showInNav: true },
        ],
        blocks: [
            block("hero", "hero", {
                title: "Bucătărie de casă, livrată caldă",
                subtitle: "Comandă online sau rezervă o masă.",
                height: "inalta",
                align: "centru",
                overlay: 55,
                ctaLabel: "Comandă acum",
                ctaHref: "#meniu",
            }),
            block("adresa", "address-bar", {
                title: "Unde livrăm?",
                hint: "Adaugă adresa ca să vezi dacă ajungem la tine",
            }),
            block("meniu", "menu-highlights", {
                title: "Preferatele clienților",
                limit: 6,
                columns: "3",
                ctaLabel: "Vezi tot meniul",
            }),
            block("avantaje", "features", {
                title: "De ce de la noi",
                columns: "3",
                items: [
                    { icon: "🍲", title: "Gătit în ziua livrării", body: "Nimic reîncălzit de ieri." },
                    { icon: "🛵", title: "Livrare în oraș", body: "Zonele acoperite sunt scrise mai jos." },
                    { icon: "🧾", title: "Bon fiscal la ușă", body: "Emis de restaurant, nu de o platformă." },
                ],
            }),
            block("poveste", "image-text", {
                title: "Cine gătește",
                body: "Scrie aici povestea restaurantului.\n\nCe se gătește zilnic, cine e în bucătărie, de unde vin ingredientele.",
                imageSide: "dreapta",
            }),
            block("rezervare", "reservations", {
                title: "Rezervă o masă",
                subtitle: "Pentru cine vine la noi, nu comandă acasă.",
            }),
            block("program", "hours", { title: "Program" }),
            block("contact", "contact", { title: "Unde ne găsești", showPhone: true, showEmail: true }),
            block("meniu-vitrine", "storefront-grid", { title: "Meniul complet", layout: "lista", showKind: true }, "meniu"),
            block("meniu-tot", "menu-highlights", { title: "Din meniu", limit: 24, columns: "3" }, "meniu"),
            block("rez-form", "reservations", { title: "Rezervă o masă" }, "rezervari"),
            block("rez-program", "hours", { title: "Program" }, "rezervari"),
            block("contact-pagina", "contact", { title: "Contact", showPhone: true, showEmail: true }, "contact"),
        ],
    },
    {
        id: "marketplace-national",
        name: "Marketplace pe mai multe orașe",
        audience: "Platformă cu restaurante din mai multe orașe.",
        description: "Pune orașele în față: acoperirea grupată pe localități, restaurantele sub ea, plus "
            + "cifrele și încrederea de care are nevoie o platformă necunoscută.",
        theme: {
            primaryColor: "#2f4bd8",
            secondaryColor: "#1b2a80",
            accentColor: "#ff7a45",
            borderRadius: "pill",
        },
        pages: [
            { slug: HOME_PAGE_SLUG, title: "Acasă" },
            { slug: "parteneri", title: "Pentru restaurante", navLabel: "Parteneri", showInNav: true },
        ],
        blocks: [
            block("hero", "hero-search", {
                title: "Mâncare livrată în orașul tău",
                subtitle: "Scrie adresa și îți arătăm ce restaurante ajung la tine.",
                addressLabel: "Unde livrăm?",
                addressCta: "Caută",
                height: "inalta",
                overlay: 60,
            }),
            block("orase", "coverage-list", {
                title: "Orașele în care livrăm",
                showFees: true,
                showMinOrder: false,
                groupByCity: true,
            }),
            block("vitrine", "storefront-grid", { title: "Restaurante", layout: "grila", columns: "3", showKind: true }),
            block("cifre", "stats", {
                items: [
                    { value: "3", label: "orașe" },
                    { value: "40+", label: "restaurante" },
                    { value: "35 min", label: "livrare medie" },
                ],
            }),
            block("pasi", "steps", {}),
            block("recenzii", "testimonials", {
                title: "Ce spun clienții",
                items: [
                    { quote: "A ajuns cald și în intervalul promis.", author: "Andrei", role: "Timișoara" },
                    { quote: "Îmi place că văd de la ce restaurant e, nu de la o platformă.", author: "Ioana", role: "Giroc" },
                ],
            }),
            block("intrebari", "faq", {
                items: [
                    { question: "Livrați în orașul meu?", answer: "Orașele acoperite sunt scrise mai sus. Scrie adresa și îți spunem imediat dacă ajungem la tine." },
                    { question: "Cine emite bonul fiscal?", answer: "Restaurantul care gătește. Datele lui complete apar la finalizarea comenzii, înainte să confirmi." },
                    { question: "Cât costă livrarea?", answer: "Taxa e a zonei în care livrăm și apare pe comandă înainte de plată." },
                ],
            }),
            block("parteneri-text", "text", {
                title: "Pentru restaurante",
                body: "Scrie aici ce oferi restaurantelor: comision, decontare, cum se leagă la sistemul lor.\n\nLasă un mod de contact vizibil.",
                narrow: true,
            }, "parteneri"),
            block("parteneri-cta", "cta-banner", {
                title: "Vrei pe platformă?",
                body: "Scrie-ne și te contactăm în aceeași zi.",
                ctaLabel: "Scrie-ne",
                tone: "brand",
            }, "parteneri"),
        ],
    },
    {
        id: "minimal-rapid",
        name: "Minimal, doar livrare",
        audience: "Când conteaza doar comanda: fără poveste, fără pagini în plus.",
        description: "Patru blocuri. Adresa, restaurantele, unde livrăm, cum se plătește. Nimic altceva.",
        theme: { primaryColor: "#111827", accentColor: "#2563eb", borderRadius: "rounded" },
        pages: [{ slug: HOME_PAGE_SLUG, title: "Acasă" }],
        blocks: [
            block("adresa", "address-bar", { title: "Unde livrăm?" }),
            block("vitrine", "storefront-grid", { title: "De unde comanzi", layout: "grila", columns: "2" }),
            block("acoperire", "coverage-list", { title: "Unde livrăm", showFees: true, groupByCity: false }),
            block("plata", "payment-note", {}),
        ],
    },
];
export function findMarketTemplate(id) {
    return MARKET_TEMPLATES.find((template) => template.id === id) ?? null;
}
// ---------------------------------------------------------------------------
// Puntea catre configul vechi
// ---------------------------------------------------------------------------
/**
 * Cum se traduc cheile din `layout.homeSections` in blocuri.
 *
 * Pietele publicate inainte de builder au doar lista aceea de siruri. Ele NU se
 * migreaza automat in baza — o migrare tacita ar fi rescris configul publicat al
 * unei piete vii — dar vitrina trebuie sa randeze ceva, iar editorul trebuie sa
 * poata porni de la ce e acum pe ecran. Traducerea se face la citire, in ambele
 * capete, din aceeasi harta.
 */
const LEGACY_SECTION_TO_BLOCK = {
    hero: "hero",
    // Sectiunea `address` randa candva formularul intreg in pagina; azi adresa are
    // bara ei, mereu prima. Cheia ramane recunoscuta ca sa nu fie citita drept
    // necunoscuta, dar nu produce bloc: bara e adaugata separat, o singura data.
    address: null,
    storefronts: "storefront-grid",
    info: "steps",
};
/**
 * Blocurile echivalente unui `homeSections` vechi. Rezultatul e un config VALID,
 * gata de randat si de editat — nu un substitut sarac.
 */
export function blocksFromLegacySections(source) {
    const list = Array.isArray(source.layout.homeSections) ? source.layout.homeSections : [];
    // Se pastreaza doar cheile care produc CEVA. `address` e recunoscuta, dar nu
    // randeaza nimic (adresa are bara ei), deci nu poate tine locul unei sectiuni:
    // o piata cu `homeSections: ["address"]` ar fi ramas cu o pagina care nu arata
    // niciun restaurant — exact comportamentul pe care pagina veche il evita cazand
    // pe lista implicita.
    // `new Set` pastreaza ordinea si scoate repetitiile. Fara el, un `homeSections`
    // cu aceeasi cheie de doua ori (nimic nu o interzice) producea doua blocuri cu
    // acelasi id, iar prima publicare din editor era refuzata cu „identificatorul
    // apare de doua ori" — un mesaj din care operatorul nu avea cum sa deduca ca
    // problema vine din configul vechi, nu din ce a facut el.
    const known = [...new Set(list.filter((key) => LEGACY_SECTION_TO_BLOCK[key] != null))];
    const sections = known.length > 0 ? known : ["hero", "storefronts", "info"];
    const text = (key, fallback) => {
        const value = source.texts?.[source.identity.defaultLocale]?.[key];
        return typeof value === "string" && value.length > 0 ? value : fallback;
    };
    const out = [
        // Bara de adresa era, in versiunea anterioara a paginii de start, dinaintea
        // tuturor sectiunilor si nu se putea muta. Ca bloc, e prima — dar de acum
        // poate fi mutata sau scoasa.
        { id: "adresa", type: "address-bar", config: defaultBlockConfig("address-bar") },
    ];
    for (const key of sections) {
        const type = LEGACY_SECTION_TO_BLOCK[key];
        if (type === null || type === undefined)
            continue;
        if (type === "hero") {
            const config = {
                ...defaultBlockConfig("hero"),
                // Coperta veche isi lua inaltimea din continut. Aici nu exista imagine (o
                // piata legacy fara `ogImageUrl`), iar o inaltime minima ar fi transformat
                // primul ecran intr-un dreptunghi gol de 260 px cu titlul lipit jos, care
                // impinge restaurantele sub marginea telefonului.
                height: "compact",
                subtitle: text("home.hero_subtitle", "Alege ce mănânci. Adresa o ceri o singură dată, iar bonul fiscal îl emite restaurantul care gătește."),
            };
            // Titlul se scrie DOAR cand piata nu are o suprascriere in `texts`. Cand o
            // are, cheia ramane absenta si randarea o rezolva pe limba activa — altfel
            // un vizitator care comuta pe engleza ar fi vazut coperta in romana.
            const hasOverride = typeof source.texts?.[source.identity.defaultLocale]?.["home.hero_title"] === "string";
            if (!hasOverride)
                config.title = source.identity.name;
            const cover = source.branding.ogImageUrl;
            if (typeof cover === "string" && cover.length > 0)
                config.imageUrl = cover;
            out.push({ id: "coperta", type: "hero", config });
            continue;
        }
        // Blocul se naste FARA titlu, desi catalogul propune unul.
        //
        // Titlurile sectiunilor vechi („De unde comanzi", „Cum functioneaza") sunt
        // suprascriabile din `texts`, iar `texts` se rezolva pe limba ACTIVA a
        // vizitatorului. Un titlu scris aici ar fi fost o valoare fixa in config:
        // fallback-ul din randare nu s-ar mai fi consultat niciodata, iar o piata
        // bilingva ar fi afisat sectiunile in romana chiar si pe engleza.
        const config = defaultBlockConfig(type);
        delete config.title;
        out.push({ id: key, type, config });
        // Sectiunea `info` continea si randul cu metodele de plata. Ca bloc separat,
        // ramane vizibil si mutabil; fara el, o piata deja publicata ar fi pierdut
        // singurul loc in care scria cum se plateste.
        if (key === "info" && source.payments.methods.length > 0) {
            out.push({ id: "plata", type: "payment-note", config: {} });
        }
    }
    return out;
}
//# sourceMappingURL=page-blocks.js.map